import { useCallback, useEffect, useMemo, useState } from "react"
import {
    emptyQuestionSpec,
    QuestionBuilder,
    QuestionControls,
} from "@nova/component-pilot-tracking-form"
import { flushSync } from "react-dom"
import { ChecklistQuestionSpec } from "@nova/schemas"
import * as Icons from "@nova/component-icons"
import { v4 } from "uuid"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { TriCheckbox } from "@nova/component-checkbox"
import { RadioButton } from "@nova/component-btn"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import classNames from "classnames"
import { ClickAwayListenerV2 } from "@nova/component-click-away-listener"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    addChecklistCreationQuestion,
    deleteChecklistCreationQuestion,
    editChecklistCreationQuestion,
    moveChecklistCreationQuestion,
    selectChecklistCreationDescription,
    selectChecklistCreationQuestions,
    selectChecklistCreationTitle,
    setChecklistCreationDescription,
    setChecklistCreationQuestions,
    setChecklistCreationTitle,
} from "./slice"
import { useObserveElementRect } from "@nova/toolkit-pure-hooks"
import { Status } from "@nova/ds-component-status"

export const ChecklistBuilder = () => {
    const dispatch = useAppDispatch()
    const questions = useAppSelector(selectChecklistCreationQuestions)
    const [editing, setEditing] = useState<"title" | "description" | "none">("none")
    const [focusedQuestionId, setFocusedQuestionId] = useState<string | null>(null)

    const checklistName = useAppSelector(selectChecklistCreationTitle)
    const checklistDescription = useAppSelector(selectChecklistCreationDescription)

    useEffect(() => {
        if (questions !== null && questions.length === 0) {
            dispatch(setChecklistCreationQuestions([emptyQuestionSpec("text")]))
        }
    }, [dispatch, questions])

    const editQuestion = useCallback<QuestionControls["editQuestion"]>(
        (id, spec) => {
            dispatch(editChecklistCreationQuestion({ id, spec }))
        },
        [dispatch]
    )
    const deleteQuestion = useCallback<QuestionControls["deleteQuestion"]>(
        (id) => {
            dispatch(deleteChecklistCreationQuestion({ id }))
        },
        [dispatch]
    )
    const moveQuestion = useCallback<QuestionControls["moveQuestion"]>(
        (id, { direction, animate = true }) => {
            const performUpdate = () => {
                dispatch(moveChecklistCreationQuestion({ id, direction }))
            }
            animate
                ? document.startViewTransition(() => {
                      flushSync(() => {
                          performUpdate()
                      })
                  })
                : performUpdate()
        },
        [dispatch]
    )
    const getNumQuestions = useCallback(() => questions?.length || 0, [questions?.length])
    const questionControls = useMemo<QuestionControls>(() => {
        return { editQuestion, deleteQuestion, moveQuestion, getNumQuestions }
    }, [deleteQuestion, editQuestion, moveQuestion, getNumQuestions])

    if (!questions) return null

    return (
        <div className="w-full flex flex-col items-center gap-4">
            <div className="flex flex-col w-full max-w-xl pl-6">
                <ClickAwayListenerV2
                    onClickAway={() => {
                        if (editing === "title") setEditing("none")
                    }}
                >
                    <div
                        className={classNames("w-full", {
                            "cursor-text": editing !== "title",
                        })}
                        onClick={() => {
                            if (editing === "title") return
                            setEditing("title")
                        }}
                    >
                        <RenamableText
                            Tag="h2"
                            id="checklist-name"
                            title={checklistName ?? "Untitled Checklist"}
                            initialTitleIsPlaceholder={!checklistName}
                            isRenaming={editing === "title"}
                            className="[&_h2]:text-[28px] [&_h2]:text-white"
                            onConfirmRenaming={(_, v) => {
                                dispatch(setChecklistCreationTitle(v))
                                setEditing("none")
                            }}
                            onCancelRenaming={() => {
                                setEditing("none")
                            }}
                        />
                    </div>
                </ClickAwayListenerV2>
                <ClickAwayListenerV2
                    onClickAway={() => {
                        if (editing === "description") setEditing("none")
                    }}
                >
                    <div
                        className={classNames("w-full", {
                            "cursor-text": editing !== "description",
                        })}
                        onClick={() => {
                            if (editing === "description") return
                            setEditing("description")
                        }}
                    >
                        <RenamableText
                            Tag="h5"
                            id="checklist-description"
                            title={checklistDescription ?? "Add a description"}
                            initialTitleIsPlaceholder={!checklistDescription}
                            isRenaming={editing === "description"}
                            className={classNames({
                                "[&_h5]:text-gray-300": editing !== "description",
                                "[&_h5]:text-white": editing === "description",
                            })}
                            onConfirmRenaming={(_, v) => {
                                dispatch(setChecklistCreationDescription(v))
                                setEditing("none")
                            }}
                            onCancelRenaming={() => {
                                setEditing("none")
                            }}
                        />
                    </div>
                </ClickAwayListenerV2>
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-xl">
                <RequiredQuestions />
                {questions.map((q, i) => (
                    <QuestionBuilder
                        key={q.id}
                        spec={q}
                        questionControls={questionControls}
                        index={i}
                        autoFocus={q.id === focusedQuestionId}
                    />
                ))}
                <AddQuestionButton
                    onClick={(q) => {
                        dispatch(addChecklistCreationQuestion({ question: q }))
                        setFocusedQuestionId(q.id)
                    }}
                />
            </div>
        </div>
    )
}

const RequiredQuestions = () => {
    return (
        <div className="flex flex-col border border-white-100 rounded-xl divide-y divide-white-100 text-white-400 w-full">
            <div className="flex items-center justify-between py-2 px-8">
                <div className="flex items-center gap-2">
                    <Icons.SmallIcon className="text-white-300" Icon={Icons.Lock} />
                    <small>Shown only on pre-flight submissions</small>
                </div>
                <Status type="neutral" className="!bg-transparent !text-white-400">
                    Required
                </Status>
            </div>
            <div className="flex flex-col py-2.5 gap-2.5 px-8">
                <div className="flex items-center gap-2">
                    <Icons.SmallIcon Icon={Icons.ShieldPerson} />
                    <h5>Who is the Pilot in Command?</h5>
                </div>

                <div className="flex items-center gap-2">
                    <Icons.SmallIcon Icon={Icons.Drone2} />
                    <h5>Which drone is being used?</h5>
                </div>
            </div>
        </div>
    )
}

const AddQuestionButton = ({ onClick }: { onClick: (q: ChecklistQuestionSpec) => void }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const { top, left, width, height, ref } = useObserveElementRect()

    const addQuestionsOptions = useMemo(
        () => [
            {
                title: "Text Input",
                onClick: () => {
                    onClick({
                        id: v4(),
                        isRequired: false,
                        type: "text",
                        question: "",
                        variant: "short",
                    })
                },
                Icon: Icons.TextSelectStart,
            },
            {
                title: "Multiple Answers",
                onClick: () => {
                    onClick({
                        id: v4(),
                        isRequired: false,
                        type: "multi-select",
                        options: ["Option 1"],
                        question: "",
                    })
                },
                Icon: () => <TriCheckbox state="checked" />,
            },
            {
                title: "Multiple Choice",
                onClick: () => {
                    onClick({
                        id: v4(),
                        isRequired: false,
                        type: "radio",
                        options: ["Option 1"],
                        question: "",
                    })
                },
                Icon: () => <RadioButton isSelected onClick={() => {}} size="small" />,
            },
            {
                title: "User Select",
                onClick: () => {
                    onClick({
                        id: v4(),
                        isRequired: false,
                        type: "user-select",
                        question: "",
                    })
                },
                Icon: Icons.AccountCircle,
            },
            {
                title: "Aircraft Select",
                onClick: () => {
                    onClick({
                        id: v4(),
                        isRequired: false,
                        type: "aircraft-select",
                        question: "",
                    })
                },
                Icon: Icons.Drone2,
            },
            {
                title: "Header",
                onClick: () => {
                    onClick({
                        id: v4(),
                        type: "header",
                        question: "",
                    })
                },
                Icon: Icons.FormatH1,
            },
            {
                title: "Presets",
                submenu: {
                    sections: [
                        [
                            {
                                title: "Pilot in Command",
                                onClick: () => {
                                    onClick({
                                        id: v4(),
                                        isRequired: false,
                                        type: "user-select",
                                        question: "Who is the Pilot in Command?",
                                    })
                                },
                            },
                            {
                                title: "Visual Observer",
                                onClick: () => {
                                    onClick({
                                        id: v4(),
                                        isRequired: false,
                                        type: "user-select",
                                        question: "Who is the Visual Observer?",
                                    })
                                },
                            },
                            {
                                title: "Payload Operator",
                                onClick: () => {
                                    onClick({
                                        id: v4(),
                                        isRequired: false,
                                        type: "user-select",
                                        question: "Who is the Payload Operator?",
                                    })
                                },
                            },
                            {
                                title: "Mission Commander",
                                onClick: () => {
                                    onClick({
                                        id: v4(),
                                        isRequired: false,
                                        type: "user-select",
                                        question: "Who is the Mission Commander?",
                                    })
                                },
                            },
                            {
                                title: "Instructor",
                                onClick: () => {
                                    onClick({
                                        id: v4(),
                                        isRequired: false,
                                        type: "user-select",
                                        question: "Who is the Instructor?",
                                    })
                                },
                            },
                        ],
                    ],
                },
            },
        ],
        [onClick]
    )

    return (
        <div className="relative w-full max-w-xl pl-5">
            <button
                ref={ref}
                className="h-8 flex gap-1 items-center justify-center w-full group"
                onClick={(e) => {
                    e.stopPropagation()
                    setIsMenuOpen((p) => !p)
                }}
            >
                <div className="flex-1 h-px bg-gray-700 group-hover:bg-gray-600 transition-colors" />
                <Icons.SmallIcon
                    className="text-white-500 group-hover:text-white group-hover:scale-110 transition-all"
                    Icon={Icons.Plus}
                />
                <div className="flex-1 h-px bg-gray-700 group-hover:bg-gray-600 transition-colors" />
            </button>
            {isMenuOpen && (
                <ContextMenu
                    className="w-[160px] -translate-x-1/2"
                    sections={[addQuestionsOptions]}
                    onBlur={() => setIsMenuOpen(false)}
                    isFixedPosition
                    top={top + height}
                    left={left + Math.round(width / 2)}
                />
            )}
        </div>
    )
}
